import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useFetch } from "../../hooks";
import { BASE_URL_TEST } from "../../utils/baseUrl";
import { urls } from "../../urls/urls";
import { useSelector } from "react-redux";

export default function AgentAssignment({ open, onClose, orderId, agentAssignId, onAssign }) {
  const [searchValue, setSearchValue] = useState("");
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const loginToken = useSelector((state) => state.user.logintoken);

  // Initialize useFetch hook for getAllSearchAgents API
  const {
    data: agentsApiData,
    error: agentsApiError,
    isLoading: agentsApiLoading,
    fetchData: fetchAgentsData,
  } = useFetch(BASE_URL_TEST + urls("getAllSearchAgents"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: loginToken ? `Bearer ${loginToken}` : "",
    },
  });

  // Initialize useFetch hook for PostAssignAgent API
  const {
    data: assignAgentApiData,
    error: assignAgentApiError,
    isLoading: assignAgentApiLoading,
    fetchData: postAssignAgentData,
  } = useFetch(BASE_URL_TEST + urls("PostAssignAgent"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: loginToken ? `Bearer ${loginToken}` : "",
    },
  });

  // Fetch agents when dialog opens or search value changes
  useEffect(() => {
    if (open) {
      // Reset selection when dialog opens
      setSelectedAgent(null);

      // Fetch agents with search parameter
      fetchAgentsData();
    }
  }, [open, searchValue]);

useEffect(() => {
  if (agentsApiError) {
    console.error("Error fetching agents:", agentsApiError);
    setAgents([]);
  } else if (agentsApiData && agentsApiData.status === true) {
    console.log(agentsApiData.data)
    // Transform API data and filter ONLY available agents
    const transformedAgents = agentsApiData.data
  ?.filter((agent) => agent.status?.toLowerCase() === "online" || agent.status?.toLowerCase() === "active")
  .map((agent) => ({
    ...agent,
    agentId: agent.agent_id || "N/A",
    agentName: agent.first_name && agent.last_name
      ? `${agent.first_name} ${agent.last_name}`.trim()
      : agent.first_name || agent.last_name || "N/A",
    phoneNo: agent.phone_number || "N/A",
    address: [agent.street, agent.landmark, agent.city]
      .filter(Boolean)
      .join(", ") || "N/A",
    pinCode: agent.pincode || "N/A",
    status: "Active",           // display-friendly label, agent is confirmed online
    orderStatus: "Available",   // safe default since only online agents pass the filter
  })) || [];
    setAgents(transformedAgents);
  } else if (agentsApiData && agentsApiData.status === false) {
    setAgents([]);
  }
}, [agentsApiData, agentsApiError]);

  // Handle assign agent API response
  useEffect(() => {
    if (assignAgentApiError) {
      console.error("Error assigning agent:", assignAgentApiError);
      setLoading(false);
      alert("Failed to assign agent. Please try again.");
    } else if (assignAgentApiData && assignAgentApiData.status === true) {
      console.log("Agent assigned successfully:", assignAgentApiData);
      setLoading(false);

      // Call parent callback with selected agent
      if (onAssign && selectedAgent) {
        onAssign(selectedAgent);
      }

      // Close dialog
      onClose();
    } else if (assignAgentApiData && assignAgentApiData.status === false) {
      console.log("Failed to assign agent:", assignAgentApiData.message);
      setLoading(false);
      alert(assignAgentApiData.message || "Failed to assign agent");
    }
  }, [assignAgentApiData, assignAgentApiError]);

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleCheckboxChange = (agent) => {
    // Only allow one agent to be selected at a time
    if (selectedAgent?.agentId === agent.agentId) {
      setSelectedAgent(null);
    } else {
      setSelectedAgent(agent);
    }
  };

  const handleAssign = () => {
    if (!selectedAgent) return;
    if (!agentAssignId) {
      alert("Agent assign ID is missing");
      return;
    }

    setLoading(true);

    // Call PostAssignAgent API
    postAssignAgentData({
      agent_assign_id: agentAssignId.toString(),
      agent_id: selectedAgent.agent_id.toString(),
    });
  };

  const filteredAgents = agents.filter(
    (agent) =>
      agent?.agentId?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
      agent?.agentName?.toLowerCase().includes(searchValue.toLowerCase()) ||
      agent?.phoneNo?.toString().includes(searchValue) ||
      agent?.address?.toLowerCase().includes(searchValue.toLowerCase()) ||
      agent?.pinCode?.toString().includes(searchValue) ||
      agent?.city?.toLowerCase().includes(searchValue.toLowerCase()) ||
      agent?.street?.toLowerCase().includes(searchValue.toLowerCase()) ||
      agent?.landmark?.toLowerCase().includes(searchValue.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Assign Agent to Order {orderId}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {/* Search Field */}
        <Box sx={{ mb: 3 }}>
          <TextField
            placeholder="Search by Agent ID, Name, Phone, or Pin Code"
            variant="outlined"
            size="small"
            fullWidth
            value={searchValue}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Agents Table */}
        {agentsApiError && (
          <Box sx={{ mb: 2, p: 2, bgcolor: "#ffebee", borderRadius: 1 }}>
            <Typography color="error">
              Error loading agents: {agentsApiError.message || "Please try again"}
            </Typography>
          </Box>
        )}
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ fontWeight: "bold" }}>
                  SELECT
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>AGENT ID</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>AGENT NAME</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>PHONE NO</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>ADDRESS</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>PIN CODE</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>ORDER STATUS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agentsApiLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading agents...</Typography>
                  </TableCell>
                </TableRow>
              ) : agents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      No agents found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAgents.map((agent) => (
                  <TableRow
                    key={agent.agentId}
                    hover
                    selected={selectedAgent?.agentId === agent.agentId}
                    sx={{
                      cursor: "pointer",
                      "&.Mui-selected": {
                        backgroundColor: "rgba(79, 70, 229, 0.08)",
                      },
                    }}
                    onClick={() => handleCheckboxChange(agent)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedAgent?.agentId === agent.agentId}
                        onChange={() => handleCheckboxChange(agent)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>{agent.agentId}</TableCell>
                    <TableCell>{agent.agentName}</TableCell>
                    <TableCell>{agent.phoneNo}</TableCell>
                    <TableCell>{agent.address}</TableCell>
                    <TableCell>{agent.pinCode}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor:
                            agent.status === "Active"
                              ? "rgba(76, 175, 80, 0.2)"
                              : "rgba(244, 67, 54, 0.2)",
                          borderRadius: "10px",
                          padding: "4px 12px",
                          color: agent.status === "Active" ? "#4CAF50" : "#F44336",
                          fontWeight: "bold",
                          display: "inline-block",
                        }}
                      >
                        {agent.status}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor:
                            agent.orderStatus === "Available"
                              ? "rgba(33, 150, 243, 0.2)"
                              : agent.orderStatus === "Busy"
                              ? "rgba(255, 152, 0, 0.2)"
                              : "rgba(158, 158, 158, 0.2)",
                          borderRadius: "10px",
                          padding: "4px 12px",
                          color:
                            agent.orderStatus === "Available"
                              ? "#2196F3"
                              : agent.orderStatus === "Busy"
                              ? "#FF9800"
                              : "#9E9E9E",
                          fontWeight: "bold",
                          display: "inline-block",
                        }}
                      >
                        {agent.orderStatus}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Selected Agent Info */}
        {selectedAgent && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "rgba(79, 70, 229, 0.05)",
              borderRadius: 2,
              border: "1px solid rgba(79, 70, 229, 0.2)",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              Selected Agent:
            </Typography>
            <Typography variant="body2">
              {selectedAgent.agentName} (ID: {selectedAgent.agentId}) - {selectedAgent.phoneNo}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          color="primary"
          disabled={!selectedAgent || loading}
          sx={{
            minWidth: 120,
          }}
        >
          {loading ? <CircularProgress size={24} /> : "Assign Agent"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
